import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  CopyPlusIcon,
  ListChecksIcon,
  PencilRulerIcon,
  WorkflowIcon,
} from 'lucide-react';

import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { NotFoundState } from '@/shared/components/NotFoundState';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { WorkflowSkeleton } from './WorkflowSkeleton';
import { traverseForward } from '../graphTraversal';
import type { TraversalCursor } from '../graphTraversal';
import {
  graphSelectionKey,
  hasGraphSelection,
  resolveGraphSelection,
  selectedNodeId,
} from '../graph/selection';
import type { GraphSelection } from '../graph/selection';
import type { WorkflowDetailSearch } from '../searchParams';
import { ExpandableText } from '@/shared/components/ExpandableText';
import { useWorkflow } from '../hooks/useWorkflow';
import { countDeclaredSteps } from '../hooks/useWorkflows';
import { CopyToWorkspaceDialog } from './CopyToWorkspaceDialog';
import { GraphDetailPanel } from './GraphDetailPanel';
import { WorkflowName } from './WorkflowName';

/**
 * The canvas is loaded on demand, and this dynamic import is what keeps it out
 * of the entry bundle. Route-level splitting alone does not: this route must
 * evaluate its search schema eagerly, and that pulls its module — and anything
 * that module imports — into the eager graph. An explicit boundary here does
 * not care how the route is split.
 */
const WorkflowGraph = lazy(() =>
  import('./WorkflowGraph').then((module) => ({
    default: module.WorkflowGraph,
  })),
);

/**
 * The workflow detail page: the read-only graph under the shared page frame.
 *
 * Selection is not state here — the selected step arrives as a prop from the
 * route's search params and leaves via the navigate callbacks, so a link to
 * "this workflow, this step" restores exactly this view (FR-047). An unknown
 * step id degrades to no selection rather than erroring: search parameters are
 * view state, and view state falls back (see searchParams.ts).
 */
export function WorkflowGraphPage({
  workspaceId,
  workflowKey,
  search,
  onSelect,
  onClearSelection,
}: {
  workspaceId: string;
  workflowKey: string;
  /** The address's view state, resolved against the workflow once it loads. */
  search: WorkflowDetailSearch;
  onSelect: (selection: NonNullable<GraphSelection>) => void;
  onClearSelection: () => void;
}) {
  const { workflow, isPending, loadFailed, refetch } = useWorkflow(
    workspaceId,
    workflowKey,
  );
  const showPlaceholder = usePendingDisplay(isPending);
  const [copyOpen, setCopyOpen] = useState(false);

  // Resolved against the workflow, so a link that outlived a rename degrades to
  // no selection rather than to a panel about nothing. Both kinds go through one
  // resolver for the same reason they are one union: they are alternatives, and
  // two independent checks would let both be true at once.
  const selection = useMemo<GraphSelection>(
    () => (workflow ? resolveGraphSelection(workflow, search) : null),
    [workflow, search],
  );
  // Which node the canvas draws as selected, if any.
  const validSelectedStepId = selectedNodeId(selection);

  // Where a fork is up to, so repeated presses cycle its branches rather than
  // walking off down the first one (FR-058). A ref, not state: nothing renders
  // from it, and re-rendering the canvas on every keystroke would be a real cost
  // for no visible change.
  const cursorRef = useRef<TraversalCursor>(null);
  useEffect(() => {
    cursorRef.current = null;
  }, [workflow?.id]);

  // `Esc` closes the selection/panel wherever focus is (FR-045). The panel is
  // not a dialog — the graph stays visible — so the page owns the key.
  //
  // The same listener carries traversal: ←/→ walk the graph against the arrows,
  // cycling a fork's targets. Safe to take here because the read-only graph sets
  // `nodesDraggable={false}`, so React Flow has no arrow-key behaviour of its
  // own to displace.
  useEffect(() => {
    // Guarded on *anything* being selected, not on a step being selected. It
    // used to be the latter, which meant `Esc` silently stopped closing the
    // panel the moment a transition or a phase could be the subject — while a
    // comment two files away still promised that it did.
    if (!hasGraphSelection(selection)) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        cursorRef.current = null;
        onClearSelection();
        return;
      }

      // Traversal walks between *steps*, so it needs one to walk from. A
      // transition or a phase closes on Esc like anything else, and leaves the
      // arrow keys alone.
      if (validSelectedStepId === undefined) return;
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      // Let the browser keep its arrows wherever text is being read or edited.
      const target = event.target as HTMLElement | null;
      if (
        target?.isContentEditable ||
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA'
      ) {
        return;
      }
      if (!workflow) return;

      const next = traverseForward(
        workflow,
        validSelectedStepId,
        cursorRef.current,
        event.key === 'ArrowRight' ? 1 : -1,
      );
      // A step with nothing downstream leaves the selection alone rather than
      // clearing it: a dead end is not a reason to lose your place.
      if (next.selectStepId === null) return;

      event.preventDefault();
      cursorRef.current = next.cursor;
      onSelect({ kind: 'step', id: next.selectStepId });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selection, validSelectedStepId, onClearSelection, onSelect, workflow]);

  // As in `BuilderPage`: the content region, not a page frame (spec 079).
  if (isPending) {
    return showPlaceholder ? (
      <WorkflowSkeleton label="Loading workflow" />
    ) : null;
  }

  if (loadFailed) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
        <p className="text-muted-foreground text-sm">
          The workflow could not be loaded.
        </p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  if (!workflow) {
    return (
      <NotFoundState
        title="Workflow not found"
        description="This workspace has no workflow with that key. It may have been renamed or removed since the link was shared."
      />
    );
  }

  const stepCount = countDeclaredSteps(workflow.phases);

  return (
    // Not the shared page frame: that frame scrolls a column of content, and
    // this page is a canvas that must own the height instead.
    <section className="flex min-h-0 flex-1 flex-col">
      {/* One toolbar row rather than a stacked title block — every pixel it
          gives up goes to the canvas. */}
      <div
        data-testid="workflow-toolbar"
        // On a phone three lines: the name, its key and step count under it,
        // then Copy and Edit sharing the width. In one line the name broke a
        // letter at a time under its key and Edit ended past the screen
        // (spec 273).
        className="border-border flex flex-none items-center gap-3 border-b px-6 py-3 max-md:flex-wrap max-md:gap-y-2 max-md:px-4"
      >
        <WorkflowIcon
          aria-hidden="true"
          className="text-muted-foreground size-4 shrink-0"
          strokeWidth={1.75}
        />
        {/* The same rename control the list uses, so a rename behaves the same
            from either surface (FR-031). */}
        <h1 className="text-foreground min-w-0 text-base font-medium tracking-tight max-md:basis-[calc(100%-1.75rem)]">
          <WorkflowName
            workspaceId={workspaceId}
            workflowId={workflow.id}
            name={workflow.name}
          />
        </h1>
        <code className="bg-muted text-muted-foreground shrink-0 rounded px-1.5 py-0.5 text-xs max-md:ms-7">
          {workflow.key}
        </code>
        <span className="text-muted-foreground flex shrink-0 items-center gap-1 text-xs">
          <ListChecksIcon
            aria-hidden="true"
            className="size-3.5"
            strokeWidth={1.75}
          />
          {stepCount} {stepCount === 1 ? 'step' : 'steps'}
        </span>
        {/* The way from looking at a workflow to changing it. Placed here
            because this page is where someone is already reading the graph and
            deciding it needs an edit. */}
        {/* Where a shared link lands (spec 205): someone reading another
            team's workflow can take a copy home from here. */}
        <div className="contents max-md:flex max-md:w-full max-md:gap-2 max-md:[&>*]:flex-1">
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto shrink-0 max-md:ml-0"
            onClick={() => setCopyOpen(true)}
          >
            <CopyPlusIcon aria-hidden="true" />
            Copy to workspace…
          </Button>
          <Link
            to="/workspaces/$workspaceId/workflows/$workflowKey/builder"
            params={{ workspaceId, workflowKey }}
            className={cn(
              buttonVariants({ variant: 'outline', size: 'sm' }),
              'shrink-0',
            )}
          >
            <PencilRulerIcon aria-hidden="true" />
            Edit
          </Link>
        </div>
      </div>

      {/*
        The description. This is the page that answers "what is this workflow
        for", so it gets the most room for it — but it no longer shows it at
        unbounded height.

        It used to, on the reasoning that the board clamps and the list
        truncates, so somewhere had to show the whole thing. What that missed is
        that the canvas is this page's subject too: a description long enough to
        matter pushed the graph down the screen, which is the same defect the
        board's clamp was introduced to fix. The clamp comes with a Show more,
        so the full text is still one press away here and the old "authored in
        the builder, legible nowhere" failure does not return — that failure was
        about text being *unreachable*, not about it being folded.

        It used to sit inside the toolbar above — one line, `truncate`, hidden
        below the `sm` breakpoint, and reachable in full only through a `title`
        tooltip. That meant the description was authored in the builder and then
        legible nowhere at all, and on a phone it was not even present.

        Rendered only when a workflow actually has one. The generic fallback
        that used to fill this slot ("A read-only view of this workflow: its
        phases, steps, prompts, and transitions") described the page to someone
        already looking at it, and it cost a line on every workflow that had
        nothing to say. A workflow with no description now spends no height on
        one, which is less than the toolbar spent before.
      */}
      {workflow.description ? (
        // The rule spans the page; the prose does not. Putting the width cap on
        // the bordered element itself would stop the border two thirds of the
        // way across and read as a torn edge.
        <div className="border-border flex-none border-b px-6 py-2.5 max-md:px-4">
          {/*
            Clamped and openable, the treatment the board settled on. This used
            to render at whatever height the text wanted, so a long description
            pushed the canvas — the subject of this page — down the screen.

            `max-w-6xl` rather than `max-w-prose`: the prose measure is right
            for a column of body text read top to bottom, and wrong for two
            lines of context above a canvas, where it left half the width empty
            and clamped text that had room to sit on the same line.

            `text-xs`, not the board's `text-sm`: this strip sits between a
            toolbar and the canvas and is the denser of the two surfaces.
          */}
          <ExpandableText
            text={workflow.description}
            id="workflow-graph-description"
            className="max-w-6xl text-xs"
          />
        </div>
      ) : null}

      {/* The positioning context for the step panel, which floats over the
          canvas so that inspecting a step never reflows the chain (FR-041). */}
      <div className="relative min-h-0 flex-1 p-4">
        <Suspense fallback={<WorkflowSkeleton label="Loading graph" />}>
          <WorkflowGraph
            workspaceId={workspaceId}
            workflow={workflow}
            selection={selection}
            onSelect={onSelect}
            onClearSelection={onClearSelection}
          />
        </Suspense>
        {selection !== null && (
          <GraphDetailPanel
            // Remounting per subject keeps no scroll position or state from the
            // previously inspected one (FR-045 swaps content in place).
            key={graphSelectionKey(selection)}
            workflow={workflow}
            selection={selection}
            onSelect={onSelect}
            onClose={onClearSelection}
          />
        )}
      </div>
      <CopyToWorkspaceDialog
        workspaceId={workspaceId}
        workflow={workflow}
        open={copyOpen}
        onOpenChange={setCopyOpen}
      />
    </section>
  );
}
